import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, X } from "lucide-react";

const SUGGESTIONS = [
  "¿Qué servicios ofrecen?",
  "¿Dónde están ubicados?",
  "Quiero cotizar mi cocina",
];

const WELCOME = {
  role: "assistant",
  content:
    "¡Hola! Soy el asistente de Toscana Cocinas Integrales. Pregúntame lo que quieras, o llámanos al +52 452 132 6712 y te atendemos personalmente.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(null);

  useEffect(() => {
    sessionId.current =
      localStorage.getItem("toscana_chat_session") ||
      `s_${Math.random().toString(36).slice(2)}${Date.now()}`;
    localStorage.setItem("toscana_chat_session", sessionId.current);
  }, []);

  useEffect(() => {
    if (open) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: msg },
      { role: "assistant", content: "" },
    ]);
    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId.current }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          const delta = JSON.parse(payload).delta;
          if (delta) {
            setMessages((m) => {
              const copy = [...m];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, content: last.content + delta };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          content:
            "Hubo un problema. Llámanos al +52 452 132 6712 y con gusto te ayudo.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-button"
        aria-label={open ? "Cerrar chat" : "Abrir chat de asistencia"}
        className="fixed bottom-5 right-5 z-[60] inline-flex h-14 w-14 items-center justify-center rounded-full bg-terracotta text-cream shadow-[0_14px_35px_rgba(13,21,38,0.3)] transition-transform duration-300 hover:scale-105"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-[60] flex h-[520px] max-h-[72vh] w-[calc(100vw-2.5rem)] max-w-sm flex-col overflow-hidden rounded-3xl border border-cream-border bg-card shadow-[0_30px_70px_rgba(13,21,38,0.3)]"
            data-testid="chat-panel"
          >
            <div className="flex items-center justify-between bg-olive px-5 py-4">
              <div>
                <p className="font-serif text-xl text-cream">Asistente Toscana</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-cream/50">
                  Responde al instante
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar chat"
                data-testid="chat-close-button"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-cream/70 transition-colors duration-200 hover:bg-cream/10 hover:text-cream"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 space-y-3 overflow-y-auto bg-cream-soft px-4 py-4"
            >
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto rounded-tr-sm bg-olive text-cream"
                      : "rounded-tl-sm bg-card text-olive"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-olive-mid [animation-delay:0ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-olive-mid [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-olive-mid [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => send(s)}
                      data-testid="chat-suggestion"
                      className="rounded-full border border-olive/20 bg-card px-3.5 py-2 text-xs text-olive transition-colors duration-200 hover:border-terracotta hover:text-terracotta"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-cream-border bg-card px-4 py-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Escribe tu pregunta..."
                data-testid="chat-input"
                className="flex-1 rounded-full border border-cream-border bg-cream-soft px-4 py-2.5 text-sm text-olive outline-none transition-colors duration-200 placeholder:text-olive-mid/50 focus:border-terracotta"
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Enviar mensaje"
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-terracotta text-cream transition-colors duration-300 hover:bg-terracotta-dark disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
